import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Avatar,
  Tooltip,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  Storefront as StorefrontIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { UpdateGfMerchantDto } from "@swagger/typescript-fetch-goflyer";
import { FieldPath, FieldValue } from "../utils/fieldChangeHelpers";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";

interface StoreHeaderProps {
  merchant?: UpdateGfMerchantDto;
  isEditing: boolean;
  isUploadingImage: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onFieldChange: (field: FieldPath, value: FieldValue) => void;
  onImageFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const StoreHeader: React.FC<StoreHeaderProps> = ({
  merchant,
  isEditing,
  isUploadingImage,
  onEdit,
  onSave,
  onCancel,
  onFieldChange,
  onImageFileSelect,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      elevation={0}
      sx={{
        mb: 4,
        background: "linear-gradient(135deg, #02b7cf 0%, #0288a3 100%)",
        color: "white",
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center">
            {isEditing ? (
              <Box sx={{ mr: 2 }}>
                <Avatar
                  src={merchant?.merchantLogo?.src}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: "white",
                    border: "2px solid rgba(255,255,255,0.3)",
                  }}
                >
                  <StorefrontIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={
                      isUploadingImage ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CloudUploadIcon />
                      )
                    }
                    size="small"
                    disabled={isUploadingImage}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.9)",
                      borderColor: "rgba(255,255,255,0.5)",
                      color: "#333",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,1)",
                        borderColor: "rgba(255,255,255,0.8)",
                      },
                      "&:disabled": {
                        backgroundColor: "rgba(255,255,255,0.7)",
                        borderColor: "rgba(255,255,255,0.3)",
                        color: "#666",
                      },
                    }}
                  >
                    {isUploadingImage
                      ? t(messages.Uploading())
                      : t(messages.Upload_Logo())}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={onImageFileSelect}
                    />
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                {merchant?.merchantLogo?.src ? (
                  <Avatar
                    src={merchant?.merchantLogo?.src}
                    sx={{
                      width: 80,
                      height: 80,
                      mr: 2,
                      bgcolor: "white",
                      border: "2px solid rgba(255,255,255,0.3)",
                    }}
                  />
                ) : (
                  <StorefrontIcon sx={{ fontSize: 48, mr: 2 }} />
                )}
              </>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {isEditing ? (
                <>
                  <TextField
                    variant="outlined"
                    size="small"
                    value={merchant?.name || ""}
                    onChange={(e) =>
                      onFieldChange("merchantName", e.target.value)
                    }
                    placeholder={t(messages.Enter_Store_Name())}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(255,255,255,0.9)",
                        fontSize: { xs: "1.75rem", sm: "2.125rem" },
                        fontWeight: 700,
                        borderRadius: 2,
                      },
                      "& .MuiOutlinedInput-input": {
                        fontSize: { xs: "1.75rem", sm: "2.125rem" },
                        fontWeight: 700,
                        color: "#333",
                      },
                    }}
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    value={merchant?.nameChinese || ""}
                    onChange={(e) =>
                      onFieldChange("merchantNameChinese", e.target.value)
                    }
                    placeholder={t(messages.Store_Name_Chinese_Optional())}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(255,255,255,0.9)",
                        fontSize: { xs: "1.1rem", sm: "1.3rem" },
                        fontWeight: 600,
                        borderRadius: 2,
                      },
                      "& .MuiOutlinedInput-input": {
                        fontSize: { xs: "1.1rem", sm: "1.3rem" },
                        fontWeight: 600,
                        color: "#333",
                      },
                    }}
                  />
                </>
              ) : (
                <>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1.75rem", sm: "2.125rem" },
                      mb: 0.5,
                    }}
                  >
                    {merchant?.name || t(messages.Store_Details())}
                  </Typography>
                  {merchant?.nameChinese && (
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "1.1rem", sm: "1.3rem" },
                        opacity: 0.9,
                        mb: 0.5,
                      }}
                    >
                      {merchant.nameChinese}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {isEditing ? (
              <>
                {" "}
                <Tooltip title={t(messages.Save_Changes())}>
                  <IconButton
                    onClick={onSave}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.2)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                    }}
                  >
                    <CheckIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t(messages.Cancel())}>
                  <IconButton
                    onClick={onCancel}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.2)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title={t(messages.Edit_Store_Details())}>
                <IconButton
                  onClick={onEdit}
                  sx={{
                    color: "white",
                    bgcolor: "rgba(255,255,255,0.2)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Typography
          variant="h6"
          sx={{
            opacity: 0.95,
            fontSize: { xs: "1rem", sm: "1.125rem" },
            fontWeight: 400,
            lineHeight: 1.6,
          }}
        >
          {" "}
          {isEditing && (!merchant?.name || merchant.name === "")
            ? t(messages.Enter_Store_Information_Below())
            : t(messages.Review_Store_Information_Google()) +
              " " +
              t(messages.You_Can_Edit_These_Details_Later())}
        </Typography>
      </CardContent>
    </Card>
  );
};
